import React, { useState } from "react";
import { ArrowLeft, Plus, X, Settings2 } from "lucide-react";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import { toastr } from "react-redux-toastr";
import varifiedIcon from "../../../assests/verifiedIcon.svg";
import "./AttributeCategoryForm.css";

const AttributeCategoryFormComponent = ({
  mode = "create",
  categoryName,
  subCategoryName,
  initialData,
  isSubmitting,
  onCreateCategory,
  onBack,
  showSuccessPopup,
  successMessage,
  onCloseSuccessPopup
}) => {
  const [step, setStep] = useState(1);
  const [categoryNameAttr, setCategoryNameAttr] = useState(initialData?.name || "");
  const [addedAttributes, setAddedAttributes] = useState(initialData?.attributes || []);
  
  // Form fields for adding attribute
  const [attrName, setAttrName] = useState("");
  const [attrValueType, setAttrValueType] = useState("Text");
  const [attrHasUnit, setAttrHasUnit] = useState("No");
  const [attrIsRequired, setAttrIsRequired] = useState("No");
  const [attrListValues, setAttrListValues] = useState("");
  const [attrUnitValues, setAttrUnitValues] = useState("");
  const [attrBooleanValue, setAttrBooleanValue] = useState("");

  const dataTypeOptions = [
    { value: "Text", label: "Text" },
    { value: "Number", label: "Number" },
    { value: "List", label: "List" },
    { value: "Boolean", label: "Boolean" }
  ];

  const getDataTypeLabel = (valueType) => {
    switch (valueType) {
      case "Text": return "Text";
      case "Number": return "Number";
      case "List": return "List";
      case "Boolean": return "Boolean";
      default: return valueType;
    }
  };

  // ✅ Check if Unit Type and Is Required should be ENABLED
  // ONLY for List type - ENABLED
  const isUnitAndRequiredEnabled = () => {
    return attrValueType === "List";
  };

  const addAttribute = () => {
    if (!attrName.trim()) {
      toastr.warning("Warning", "Please enter attribute name");
      return;
    }
    
    // Validate list values if value type is List
    if (attrValueType === "List" && !attrListValues.trim()) {
      toastr.warning("Warning", "Please enter list values");
      return;
    }
    
    // Validate Boolean values if value type is Boolean
    if (attrValueType === "Boolean" && !attrBooleanValue.trim()) {
      toastr.warning("Warning", "Please select Yes/No for Boolean");
      return;
    }
    
    // ✅ Validate Unit Values when Unit Type is Yes
    if (attrValueType === "List" && attrHasUnit === "Yes" && !attrUnitValues.trim()) {
      toastr.warning("Warning", "Please enter unit values");
      return;
    }
    
    const newAttribute = {
      id: Date.now().toString(),
      priority: addedAttributes.length + 1,
      name: attrName.trim(),
      valueType: attrValueType,
      unitType: attrHasUnit,
      required: attrIsRequired,
      listValues: attrValueType === "List" ? attrListValues : "",
      unitValues: (attrValueType === "List" && attrHasUnit === "Yes") ? attrUnitValues : "", // ✅ Store unit values only when Unit Type is Yes
      booleanValue: attrValueType === "Boolean" ? attrBooleanValue : "",
    };

    setAddedAttributes([...addedAttributes, newAttribute]);
    
    // Reset form
    setAttrName("");
    setAttrValueType("Text");
    setAttrHasUnit("No");
    setAttrIsRequired("No");
    setAttrListValues("");
    setAttrUnitValues("");
    setAttrBooleanValue("");
    
    toastr.success("Success", "Attribute added successfully");
  };

  const removeAttribute = (index) => {
    const updated = addedAttributes.filter((_, idx) => idx !== index);
    const reassigned = updated.map((attr, idx) => ({
      ...attr,
      priority: idx + 1,
    }));
    setAddedAttributes(reassigned);
    toastr.info("Removed", "Attribute removed");
  };

  const handleNext = () => {
    if (!categoryNameAttr.trim()) {
      toastr.warning("Warning", "Please enter category name");
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      onBack();
    }
  };

  const handleSave = () => {
    if (addedAttributes.length === 0) {
      toastr.warning("Warning", "Please add at least one attribute");
      return;
    }
    onCreateCategory({
      name: categoryNameAttr,
      attributes: addedAttributes,
    });
  };

  const handleUnitTypeChange = (e) => {
    const value = e.target.value;
    setAttrHasUnit(value);
    // ✅ Reset unit values when Unit Type is No
    if (value === "No") {
      setAttrUnitValues("");
    }
  };

  const handleRequiredChange = (e) => {
    setAttrIsRequired(e.target.value);
  };

  const isSaveDisabled = addedAttributes.length === 0 || isSubmitting;

  // Format list values for display
  const formatListValues = (listValues) => {
    if (!listValues) return [];
    return listValues.split(',').map(v => v.trim()).filter(v => v);
  };

  return (
    <div className="acf-main-container">
      <Sidebar />
      
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="acf-success-overlay">
          <div className="acf-success-popup">
            <div className="cat-attr-success-icon">
              <img src={varifiedIcon} alt="success" />
            </div>
            <h3 className="acf-success-title">Created Successfully!</h3>
            <p className="acf-success-message">{successMessage}</p>
            <button className="acf-success-button" onClick={onCloseSuccessPopup}>
              Done
            </button>
          </div>
        </div>
      )}
      
      <div className="acf-content-wrapper">
        {/* Header */}
        <div className="acf-header">
          <button className="acf-header-back-btn" onClick={handleBack}>
            <ArrowLeft size={20} />
          </button>
          <div className="acf-header-info">
            <h1 className="acf-header-title">Add Attributes Category</h1>
            <div className="acf-header-badges">
              <span className="acf-badge-category">Parent Category: {categoryName}</span>
              {subCategoryName && (
                <span className="acf-badge-subcategory">Sub Category: {subCategoryName}</span>
              )}
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="acf-step-indicator">
          <div className={`acf-step-item ${step === 1 ? 'active' : ''}`}>
            <div className="acf-step-number">1</div>
            <div className="acf-step-label">Category Name</div>
          </div>
          <div className="acf-step-line"></div>
          <div className={`acf-step-item ${step === 2 ? 'active' : ''}`}>
            <div className="acf-step-number">2</div>
            <div className="acf-step-label">Add Attributes</div>
          </div>
        </div>

        {/* Step 1: Category Name */}
        {step === 1 && (
          <div className="acf-step-content acf-step1-content">
            <div className="acf-form-card">
              <div className="acf-form-group">
                <label className="acf-form-label">
                  Attribute Category Name <span className="acf-required-star">*</span>
                </label>
                <input
                  type="text"
                  className="acf-form-input"
                  placeholder="Eg. Processor, Connectivity, Power"
                  value={categoryNameAttr}
                  onChange={(e) => setCategoryNameAttr(e.target.value)}
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              <div className="acf-form-actions">
                <button className="acf-cancel-btn" onClick={onBack} disabled={isSubmitting}>
                  Cancel
                </button>
                <button 
                  className="acf-next-btn"
                  onClick={handleNext}
                  disabled={!categoryNameAttr.trim() || isSubmitting}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Add Attributes */}
        {step === 2 && (
          <div className="acf-step-content">
            <div className="acf-two-column-layout">
              {/* Left Column - Add Attribute Form */}
              <div className="acf-left-column">
                <div className="acf-form-card acf-add-attr-card">
                  <h3 className="acf-section-title">
                    1. Add New Attribute to <span style={{color:"#14AE78"}}>{categoryNameAttr}</span>
                  </h3>
                  
                  <div className="acf-add-attr-form-scroll">
                    <div className="acf-form-group">
                      <label className="acf-form-label">
                        Attribute Name <span className="acf-required-star">*</span>
                      </label>
                      <input
                        type="text"
                        className="acf-form-input"
                        placeholder="Eg. Color, Weight, RAM"
                        value={attrName}
                        onChange={(e) => setAttrName(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="acf-form-group">
                      <label className="acf-form-label">Value Type</label>
                      <select
                        className="acf-form-select"
                        value={attrValueType}
                        onChange={(e) => {
                          const newValueType = e.target.value;
                          setAttrValueType(newValueType);
                          // ✅ Reset unit related fields when value type changes
                          if (newValueType !== "List") {
                            setAttrHasUnit("No");
                            setAttrUnitValues("");
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        {dataTypeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* List Values - Only show when Value Type is List */}
                    {attrValueType === "List" && (
                      <div className="acf-form-group">
                        <label className="acf-form-label">
                          List Values <span className="acf-required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="acf-form-input"
                          placeholder="Red, Blue, Green, Yellow"
                          value={attrListValues}
                          onChange={(e) => setAttrListValues(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <p className="acf-hint-text">
                          Separate values by comma (e.g., Red, Blue, Green)
                        </p>
                        {/* Preview of options */}
                        {attrListValues && (
                          <div className="acf-options-preview">
                            <span className="acf-preview-label">Preview:</span>
                            {formatListValues(attrListValues).map((opt, idx) => (
                              <span key={idx} className="acf-option-tag">{opt}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Boolean Values - Only show when Value Type is Boolean */}
                    {attrValueType === "Boolean" && (
                      <div className="acf-form-group">
                        <label className="acf-form-label">
                          Select Value <span className="acf-required-star">*</span>
                        </label>
                        <div className="acf-radio-group acf-boolean-radio-group">
                          <label className={`acf-radio-option ${attrBooleanValue === "Yes" ? 'active' : ''}`}>
                            <input
                              type="radio"
                              className="acf-radio-input"
                              name="booleanValue"
                              value="Yes"
                              checked={attrBooleanValue === "Yes"}
                              onChange={(e) => setAttrBooleanValue(e.target.value)}
                              disabled={isSubmitting}
                            />
                            <span>Yes</span>
                          </label>
                          <label className={`acf-radio-option ${attrBooleanValue === "No" ? 'active' : ''}`}>
                            <input
                              type="radio"
                              className="acf-radio-input"
                              name="booleanValue"
                              value="No"
                              checked={attrBooleanValue === "No"}
                              onChange={(e) => setAttrBooleanValue(e.target.value)}
                              disabled={isSubmitting}
                            />
                            <span>No</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Unit Type - ONLY ENABLED for List type */}
                    <div className="acf-form-group">
                      <label className="acf-form-label">Unit Type</label>
                      <div className="acf-radio-group">
                        <label className={`acf-radio-option ${attrHasUnit === "Yes" ? 'active' : ''} ${!isUnitAndRequiredEnabled() ? 'disabled' : ''}`}>
                          <input
                            type="radio"
                            className="acf-radio-input"
                            name="unitType"
                            value="Yes"
                            checked={attrHasUnit === "Yes"}
                            onChange={handleUnitTypeChange}
                            disabled={!isUnitAndRequiredEnabled() || isSubmitting}
                          />
                          <span>Yes</span>
                        </label>
                        <label className={`acf-radio-option ${attrHasUnit === "No" ? 'active' : ''} ${!isUnitAndRequiredEnabled() ? 'disabled' : ''}`}>
                          <input
                            type="radio"
                            className="acf-radio-input"
                            name="unitType"
                            value="No"
                            checked={attrHasUnit === "No"}
                            onChange={handleUnitTypeChange}
                            disabled={!isUnitAndRequiredEnabled() || isSubmitting}
                          />
                          <span>No</span>
                        </label>
                      </div>
                     
                      {isUnitAndRequiredEnabled() && (
                        <p className="acf-hint-text">
                          Select Yes if this attribute has a unit (e.g., GB, KG)
                        </p>
                      )}
                    </div>

                    {/* ✅ Unit Values - Only show when Unit Type is Yes AND value type is List */}
                    {attrHasUnit === "Yes" && isUnitAndRequiredEnabled() && (
                      <div className="acf-form-group">
                        <label className="acf-form-label">
                          Unit Values <span className="acf-required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="acf-form-input"
                          placeholder="GB, TB, MB, KG, GM"
                          value={attrUnitValues}
                          onChange={(e) => setAttrUnitValues(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <p className="acf-hint-text">Separate values by comma (e.g., GB, TB, MB)</p>
                        {/* Preview of unit values */}
                        {attrUnitValues && (
                          <div className="acf-options-preview">
                            <span className="acf-preview-label">Preview:</span>
                            {attrUnitValues.split(',').map((v, idx) => (
                              <span key={idx} className="acf-option-tag">{v.trim()}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Is Required - ONLY ENABLED for List type */}
                    <div className="acf-form-group">
                      <label className="acf-form-label">Is Required?</label>
                      <div className="acf-radio-group">
                        <label className={`acf-radio-option ${attrIsRequired === "Yes" ? 'active' : ''}`}>
                          <input
                            type="radio"
                            className="acf-radio-input"
                            name="isRequired"
                            value="Yes"
                            checked={attrIsRequired === "Yes"}
                            onChange={handleRequiredChange}
                            disabled={isSubmitting}
                          />
                          <span>Yes</span>
                        </label>
                        <label className={`acf-radio-option ${attrIsRequired === "No" ? 'active' : ''}`}>
                          <input
                            type="radio"
                            className="acf-radio-input"
                            name="isRequired"
                            value="No"
                            checked={attrIsRequired === "No"}
                            onChange={handleRequiredChange}
                            disabled={isSubmitting}
                          />
                          <span>No</span>
                        </label>
                      </div>
                    </div>

                    <button
                      className="acf-add-attr-btn"
                      onClick={addAttribute}
                      disabled={
                        !attrName.trim() || 
                        (attrValueType === "List" && !attrListValues.trim()) ||
                        (attrValueType === "Boolean" && !attrBooleanValue.trim()) ||
                        (attrValueType === "List" && attrHasUnit === "Yes" && !attrUnitValues.trim()) ||
                        isSubmitting
                      }
                    >
                      <Plus size={16} /> Add to Category
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column - Added Attributes List */}
              <div className="acf-right-column">
                <div className="acf-form-card acf-attributes-list-card">
                  <div className="acf-attributes-header">
                    <h3 className="acf-section-title">2. Added Attributes List</h3>
                    <span className="acf-attr-count-badge">{addedAttributes.length} attributes</span>
                  </div>

                  <div className="acf-table-responsive">
                    <table className="acf-attributes-table">
                      <thead>
                        <tr>
                          <th className="acf-table-header acf-priority-col">Pri</th>
                          <th className="acf-table-header">Attribute Name</th>
                          <th className="acf-table-header">Value Type</th>
                         
                          <th className="acf-table-header">Required?</th>
                          <th className="acf-table-header acf-action-col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addedAttributes.length === 0 ? (
                          <tr className="acf-empty-table-row">
                            <td colSpan="6" className="acf-empty-table-cell">
                              <div className="acf-empty-attributes">
                                <div className="acf-required-warning">
                                  <span className="acf-warning-icon">⚠️</span>
                                  <span className="acf-warning-text">At least one attribute is required to create category</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          addedAttributes.map((attr, idx) => (
                            <tr key={attr.id}>
                              <td className="acf-table-cell acf-priority-cell">{attr.priority}</td>
                              <td className="acf-table-cell acf-attr-name-cell">{attr.name}</td>
                              <td className="acf-table-cell">
                                <span className="acf-value-type-badge">
                                  {getDataTypeLabel(attr.valueType)}
                                </span>
                              </td>
                              
                              <td className="acf-table-cell">
                                <span className={`acf-required-badge ${attr.required === "Yes" ? 'yes' : 'no'}`}>
                                  {attr.required}
                                </span>
                              </td>
                              <td className="acf-table-cell acf-action-col">
                                <button
                                  className="acf-remove-btn"
                                  onClick={() => removeAttribute(idx)}
                                  title="Remove Attribute"
                                >
                                  <X size={14} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="acf-form-actions acf-step2-actions">
                    <button className="acf-cancel-btn" onClick={handleBack} disabled={isSubmitting}>
                      Back
                    </button>
                    <button
                      className={`acf-submit-btn ${!isSaveDisabled ? 'active' : ''}`}
                      onClick={handleSave}
                      disabled={isSaveDisabled}
                    >
                      {isSubmitting ? 'Creating...' : 'Save & Create Category'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttributeCategoryFormComponent;